import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Badges or record numbers shown beside the title. */
  headerExtra?: ReactNode;
  /** Replaces the default footer entirely. */
  footer?: ReactNode;
  /** Shown on the left of the default footer, e.g. a "Learn more" link. */
  footerStart?: ReactNode;
  closeLabel?: string;
  /** Hides the default close button when `footer` handles it. */
  hideFooter?: boolean;
  /** Blocks Escape and outside-click — for a modal mid-operation. */
  dismissible?: boolean;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}

/**
 * The general-purpose dialog: previews, confirmations with detail, pickers,
 * anything that is not a form.
 *
 * `FormModal` builds on the same primitives but hosts a real `<form>` element, and
 * `ConfirmDialog` narrows this to the yes/no case. Three components rather than one
 * with a `mode` prop, because the submit semantics genuinely differ — conflating
 * them is how "Enter" ends up doing nothing on a form.
 */
export const Modal = ({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  headerExtra,
  footer,
  footerStart,
  closeLabel = 'Close',
  hideFooter = false,
  dismissible = true,
  children,
  className,
  bodyClassName,
}: ModalProps) => (
  <Dialog
    open={open}
    onOpenChange={(next) => {
      if (!dismissible && !next) return;
      onOpenChange(next);
    }}
  >
    <DialogContent
      size={size}
      className={className}
      onInteractOutside={(event) => {
        if (!dismissible) event.preventDefault();
      }}
      onEscapeKeyDown={(event) => {
        if (!dismissible) event.preventDefault();
      }}
    >
      <DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <DialogTitle className="flex-1">{title}</DialogTitle>
          {headerExtra}
        </div>
        {description ? <DialogDescription>{description}</DialogDescription> : null}
      </DialogHeader>

      <DialogBody className={cn(bodyClassName)}>{children}</DialogBody>

      {hideFooter ? null : (
        <DialogFooter className="sm:justify-between">
          <div className="flex items-center gap-2">{footerStart}</div>
          {footer ?? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {closeLabel}
            </Button>
          )}
        </DialogFooter>
      )}
    </DialogContent>
  </Dialog>
);
